
import React from 'react';
import { Heart, Share2 } from 'lucide-react';
import { ProductBadges } from '../ui/ProductBadges';
import { motion } from 'framer-motion';
import TechieScoreGauge from '../ui/TechieScoreGauge';
import { getProductTitle, getProductDescription, getTechieScore, getTechieScoreSummary, getBoughtCount } from '../../utils/dataFallbacks';


export function ProductHeader({ 
  product, 
  isWishlisted, 
  onWishlistToggle, 
  onShare, 
  showWishlistToast,
  wishlistLoaded 
}) {
  const { brand, rating = 4.3, review_count = 1847, bought_count } = product;
  
  // Use enriched data with fallbacks
  const title = getProductTitle(product);
  const description = getProductDescription(product);
  const techieScore = getTechieScore(product);
  const techieScoreSummary = getTechieScoreSummary(product);

  return (
    <div className="space-y-6">
      {/* Product Badges */}
      <div className="absolute top-4 left-4">
        <ProductBadges product={product} />
      </div>

      {/* Title and Brand */}
      <div>
        <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 mt-1">{brand}</p>
        
        {/* Description */}
        {description && (
          <div className="mt-3">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{description}</p>
          </div>
        )}
        
        {/* Purchase Activity */}
        {bought_count && (
          <div className="mt-2">
            <span className="text-sm text-green-600 dark:text-green-400 font-medium">
              <span aria-hidden="true">🔥</span> {bought_count}
            </span>
          </div>
        )}
        
        {/* TechieDay Score */}
        <div className="flex items-center gap-6 mt-4">
          <TechieScoreGauge 
            score={techieScore} 
            size={100} 
            summary={techieScoreSummary}
          />
          <div className="flex flex-col">
            <span className="text-sm text-gray-500 dark:text-gray-400">TechieDay Score</span>
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{techieScore.toFixed(1)}/10</span>
            <span className="text-gray-500 dark:text-gray-400">({review_count.toLocaleString()} reviews)</span>
            {techieScoreSummary && (
              <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-xs">
                {techieScoreSummary}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button 
          onClick={onWishlistToggle}
          disabled={!wishlistLoaded}
          className={`flex-1 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
            isWishlisted 
              ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-300 dark:border-red-600' 
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
          } ${!wishlistLoaded ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-analytics-id="wishlist-toggle"
        >
          <Heart size={18} className={isWishlisted ? 'fill-current' : ''} />
          {!wishlistLoaded ? 'Loading...' : (isWishlisted ? 'In Wishlist' : 'Add to Wishlist')}
        </button>
        <button 
          onClick={onShare}
          className="flex-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2 relative"
          data-analytics-id="share-button"
        >
          <Share2 size={18} />
          Share
        </button>
      </div>

      {/* Wishlist Success Toast */}
      {showWishlistToast && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50"
          data-analytics-id="wishlist-toast"
        >
          ✅ {isWishlisted ? 'Added to' : 'Removed from'} wishlist!
        </motion.div>
      )}
    </div>
  );
}
